import axiosInstance, { axiosMultipart } from './axios';
import PaginationData from 'types/models/PaginationData';
import Product, { ClothingSizeTypeStock } from 'types/models/Product';
import ProductCategory from 'types/models/ProductCategory';
import { parseImages } from './imageServices';
import ProductCategoryData from 'types/requests/ProductCategoryData';
import ClothingSizeType from 'types/models/ClothingSizeType';
import TypeEntry from 'types/models/TypeEntry';
import { parseTomorrowPlans } from './venuesServices';
import joinURLParams from 'utils/joinUrlParams';
import TomorrowGlobalProduct from 'types/models/TomorrowGlobalProduct';

export const formToFormData = (request: Record<string, any>): FormData => {
  const formData = new FormData();
  for (const key in request) {
    if (![null, undefined].includes(request[key])) {
      if (typeof request[key] === 'object') {
        const object = request[key];
        for (const item in object) {
          if (object[item]) {
            if (Object.hasOwnProperty.call(object, item)) {
              // Ensure it's not an inherited property
              formData.append(item, object[item]);
            }
          }
        }
      }
      if (typeof request[key] === 'boolean') {
        const value = request[key];
        formData.append(key, value ? '1' : '0');
      } else {
        const value = request[key];
        formData.append(key, value);
      }
    }
  }

  return formData;
};

export const getProducts = async (commerceId: string, page?: number): Promise<PaginationData<Product> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `commerces/${commerceId}/products?page=${page ?? 1}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseProducts(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getProductsSoldByCommerce = async (
  commerceId: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  body: any,
): Promise<Product[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'POST',
      url: `/billing/products/${commerceId}`,
      data: body,
    });
    return parseProducts(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getProductsByCommerce = async (commerceId: string): Promise<Product[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `/commerces/${commerceId}/products/all`,
    });
    return parseProducts(data);
  } catch (error) {
    console.log('getProductsByCommerce error', error);
    throw error;
  }
};

export const getAllSuggestedProducts = async (commerceId: string): Promise<Product[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `commerces/${commerceId}/suggested-products/all`,
    });
    return parseProducts(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};
export const searchProducts = async (
  commerceId: string,
  searchTerm: string,
  page?: number,
): Promise<PaginationData<Product> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `products/${commerceId}/search?page=${page ?? 1}&productName=${searchTerm}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseProducts(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getProductsByBenefitLocation = async (location: string, locationId: string): Promise<Product[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `products/get-products-for-benefit`,
      data: {
        location,
        locationId,
      },
    });
    return parseProducts(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getProduct = async (productId: string): Promise<Product | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `products/${productId}`,
    });
    return parseProduct(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeProduct = async (id: string, formData: FormData): Promise<Product | null> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `commerces/${id}/products`,
      data: formData,
    });
    return parseProduct(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateProduct = async (productId: string, formData: FormData): Promise<Product | null> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `products/${productId}?_method=put`,
      data: formData,
    });
    return parseProduct(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const deleteProduct = async (productId: string): Promise<Product | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'delete',
      url: `products/${productId}`,
    });
    return parseProduct(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getProductCategoriesWithPagination = async (
  page?: number,
  searchTerm?: string,
): Promise<PaginationData<ProductCategory> | null> => {
  const url = joinURLParams({ page: page ?? 1, searchTerm }, 'product-categories');

  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseProductCategories(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getProductCategories = async (): Promise<ProductCategory[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'product-categories/all',
    });
    return parseProductCategories(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getProductTypes = async (): Promise<TypeEntry[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'products/types/all',
    });
    return parseTypes(data);
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getClothingSizeTypes = async (): Promise<ClothingSizeType[] | []> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'products/clothing-size-types/all',
    });
    return parseClothingSizeTypes(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getProductsSheet = async (lang: string): Promise<Blob | null> => {
  try {
    const url = 'products/in-all-commerces' + '?lang=' + lang;
    const { data } = await axiosInstance({
      method: 'get',
      url,
      responseType: 'blob',
    });

    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const sendMassiveProductsLoad = async (payload: {
  products: any[];
  lang: string;
}): Promise<{
  error: {
    errorKey: string;
    errorValue: string;
    errorsValues?: Record<string, any>[];
    line?: number;
    product?: Product;
  } | null;
  lastSuccessRow: { line: number; product: Product } | null;
}> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'products/massive-upload',
      data: payload,
    });

    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getGuarnicionesAndExtrasProducts = async (
  commerceId: string,
  typeId: string | number,
): Promise<Product[]> => {
  const url = `commerces/${commerceId}/guarniciones-and-extras-products/all?type=${typeId}`;
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
};

const parseClothingSizeTypes = (clothingSizeTypes: any[]) => {
  return clothingSizeTypes.map((clothingSizeType) => ({
    id: clothingSizeType.id,
    description: clothingSizeType.description,
  }));
};

export const getProductCategory = async (productCategoryid: string): Promise<ProductCategory | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `product-categories/${productCategoryid}`,
    });
    return parseProductCategory(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeProductCategory = async (productCategory: ProductCategoryData): Promise<ProductCategory | null> => {
  const body = formToFormData(productCategory);
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'product-categories',
      data: body,
    });
    return parseProductCategory(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateProductCategory = async (productCategory: ProductCategoryData): Promise<ProductCategory | null> => {
  try {
    const body = formToFormData(productCategory);
    const { data } = await axiosInstance({
      method: 'post',
      url: `product-categories/${productCategory.id}?_method=put`,
      data: body,
    });
    return parseProductCategory(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getAllTomorrowProductsOnAllVenueCommercesbyCommerceId = async (
  commerceId: string,
  tomorrowGlobalProductId?: string,
): Promise<Product[] | null> => {
  try {
    let url = `products/all-tomorrow-products-on-all-venue-commerces/by-commerce/${commerceId}`;
    if (tomorrowGlobalProductId) url = url.concat(`?tomorrowGlobalProductId=${tomorrowGlobalProductId}`);
    const { data } = await axiosInstance({
      method: 'get',
      url: url,
    });
    return parseProducts(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const parseProducts = (products: any[]): Product[] => {
  return products.map((product) => parseProduct(product));
};

const parseProductIds = (product: any[]): string[] => {
  const productIds = product.map((product) => product.id);
  return productIds;
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const parseProduct = (product: any): Product => {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    price: product.price,
    estimatedPreparationTime: product.estimated_preparation_time ?? undefined,
    enabled: product.enabled === 1,
    stock: product.stock,
    minimunStock: product.minimun_stock,
    amountSold: product.amount_sold,
    isPromotion: product.is_promotion === 1,
    isClothing: product.is_clothing === 1,
    isSuggested: product.is_suggested === 1,
    isUsedForTomorrowBenefit: product.is_used_for_tomorrow_benefit === 1,
    suggestedType: product.suggested_type,
    suggestedProductsApplied: product.suggested_by_products
      ? parseProductIds(product.suggested_by_products)
      : undefined,
    minimumAmount: product.minimum_amount,
    typeId: product.type_id,
    showInList: product.show_in_list === 1,
    clothingSizeStocks: product.clothing_sizes_stocks ? parseClothingSizeTypeStocks(product.clothing_sizes_stocks) : [],
    maxirestCode: product.maxirest_code ?? undefined,
    promotionBannerBackgroundColor: product.promotion_banner_background_color ?? undefined,
    promotionBannerColorTitle: product.promotion_banner_color_title ?? undefined,
    category: product.category ? parseProductCategory(product.category) : undefined,
    images: product.images ? parseImages(product.images) : undefined,
    guarniciones: product.guarniciones ? parseProductIds(product.guarniciones) : undefined,
    extras: product.extras ? parseProductIds(product.extras) : undefined,
    tomorrowGlobalProduct: product.tomorrow_global_product
      ? parseTomorrowGlobalProduct(product.tomorrow_global_product)
      : undefined,
    commerceName: product.commerce_name ?? undefined,
    commerceId: product?.pivot?.commerce_id ?? undefined,
  };
};

const parseTomorrowGlobalProduct = (tomorrowGlobalProduct: any): TomorrowGlobalProduct => {
  return {
    id: tomorrowGlobalProduct.id,
    amountInStockAvailableForTomorrowBenefit:
      tomorrowGlobalProduct.amount_in_stock_available_for_tomorrow_benefit ?? undefined,
    tomorrowPlans: tomorrowGlobalProduct.tomorrow_plans ? parseTomorrowPlans(tomorrowGlobalProduct.tomorrow_plans) : [],
    tomorrowBenefitDiscountPercentage: tomorrowGlobalProduct.tomorrow_benefit_discount_percentage ?? undefined,
    products: tomorrowGlobalProduct.products ? parseProducts(tomorrowGlobalProduct.products) : [],
  };
};

const parseClothingSizeTypeStocks = (clothingSizeStocks: any[]): ClothingSizeTypeStock[] => {
  return clothingSizeStocks.map((clothingSizeStock) => ({
    id: clothingSizeStock.clothing_size_type_id,
    description: clothingSizeStock.clothing_size_type.description,
    stock: clothingSizeStock.stock,
  }));
};

const parseProductCategories = (categories: any): ProductCategory[] => {
  return categories.map((category: any): ProductCategory => parseProductCategory(category));
};

const parseProductCategory = (data: any): ProductCategory => {
  return {
    description: data.description,
    id: data.id,
    requiresIdentification: data.requires_identification,
    image: { type: 1, path: data.path },
  };
};

const parseTypes = (types: any): TypeEntry[] => {
  return types.map(
    (type: any): TypeEntry => ({
      id: type.id,
      description: type.description,
    }),
  );
};
